import React, { useEffect, useRef } from 'react';
import { globalImpact } from '../../data/mock';

const locations = [
  { name: 'Europe', flag: '🇪🇺', position: { top: '28%', left: '52%' } },
  { name: 'USA', flag: '🇺🇸', position: { top: '35%', left: '18%' } },
  { name: 'India', flag: '🇮🇳', position: { top: '42%', left: '72%' } },
  { name: 'South Africa', flag: '🇿🇦', position: { top: '68%', left: '55%' } },
];

const GlobalImpactSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Map Visual */}
          <div className="reveal-scale order-2 lg:order-1">
            <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 overflow-hidden">
              {/* World Map Image */}
              <div className="relative aspect-[4/3]">
                <img 
                  src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80" 
                  alt="World Map"
                  className="absolute inset-0 w-full h-full object-cover opacity-40 rounded-2xl"
                />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-gray-900/50 rounded-2xl"></div>
                
                {/* Animated location pins */}
                {locations.map((loc, index) => (
                  <div 
                    key={loc.name}
                    className="absolute z-10"
                    style={{ top: loc.position.top, left: loc.position.left }}
                  >
                    <div className="relative">
                      <div 
                        className="absolute inset-0 w-4 h-4 bg-[#ff6b35] rounded-full animate-ping opacity-50"
                        style={{ animationDelay: `${index * 0.3}s` }}
                      ></div>
                      <div className="relative w-4 h-4 bg-[#ff6b35] rounded-full border-2 border-white shadow-lg"></div>
                    </div>
                  </div>
                ))}

                {/* Content overlay */}
                <div className="relative h-full flex flex-col justify-between p-4 z-20">
                  <div className="text-white">
                    <h3 className="text-2xl font-bold mb-2">Global Presence</h3>
                    <p className="text-gray-300 text-sm">Serving clients across 4 continents</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-auto">
                    {locations.map((loc) => (
                      <div 
                        key={loc.name} 
                        className="bg-gray-800/80 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3 border border-gray-700/50 hover:border-[#ff6b35]/50 transition-colors"
                      >
                        <span className="text-xl">{loc.flag}</span>
                        <span className="text-white font-medium text-sm">{loc.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="reveal order-1 lg:order-2">
            <span className="inline-block px-4 py-1.5 bg-[#ff6b35]/10 text-[#ff6b35] text-sm font-semibold rounded-full mb-4">
              Global Reach
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Delivering Excellence Worldwide
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {globalImpact.description}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#ff6b35]">4+</div>
                <div className="text-gray-500 text-sm">Continents</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#ff6b35]">30+</div>
                <div className="text-gray-500 text-sm">Clients</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#ff6b35]">24/7</div>
                <div className="text-gray-500 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalImpactSection;
